/**
 * Badge Component
 *
 * A small status indicator/label component.
 */

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/utils/cn';

// ============================================
// Badge Variants
// ============================================

const badgeVariants = cva(
  [
    'inline-flex items-center justify-center',
    'rounded-full font-medium',
    'whitespace-nowrap',
  ],
  {
    variants: {
      variant: {
        default: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-100',
        primary: 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400',
        success: 'bg-success-50 text-success-700 dark:bg-success/20 dark:text-success',
        warning: 'bg-warning-50 text-warning-700 dark:bg-warning/20 dark:text-warning',
        danger: 'bg-danger-50 text-danger-700 dark:bg-danger/20 dark:text-danger',
        info: 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400',
        outline: 'border border-border dark:border-border-dark text-foreground dark:text-foreground-dark',
      },
      size: {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-0.5 text-xs',
        lg: 'px-3 py-1 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

// ============================================
// Badge Props
// ============================================

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Dot indicator before text */
  dot?: boolean;
}

// ============================================
// Badge Component
// ============================================

export function Badge({ className, variant, size, dot, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant, size }), className)} {...props}>
      {dot && (
        <span
          className={cn(
            'mr-1.5 h-1.5 w-1.5 rounded-full',
            variant === 'success' && 'bg-success',
            variant === 'warning' && 'bg-warning',
            variant === 'danger' && 'bg-danger',
            variant === 'primary' && 'bg-primary',
            variant === 'info' && 'bg-primary',
            (variant === 'default' || variant === 'outline' || !variant) && 'bg-current'
          )}
        />
      )}
      {children}
    </span>
  );
}

// ============================================
// Status Badge - Convenience Component
// ============================================

export type StatusType = 'active' | 'inactive' | 'pending' | 'suspended' | 'terminated';

const statusConfig: Record<StatusType, { variant: BadgeProps['variant']; label: string }> = {
  active: { variant: 'success', label: 'Active' },
  inactive: { variant: 'default', label: 'Inactive' },
  pending: { variant: 'warning', label: 'Pending' },
  suspended: { variant: 'warning', label: 'Suspended' },
  terminated: { variant: 'danger', label: 'Terminated' },
};

export interface StatusBadgeProps extends Omit<BadgeProps, 'variant'> {
  status: StatusType | string;
}

export function StatusBadge({ status, ...props }: StatusBadgeProps) {
  const lowerStatus = status.toLowerCase() as StatusType;
  const config = statusConfig[lowerStatus] || { variant: 'default' as const, label: status };

  return (
    <Badge variant={config.variant} dot {...props}>
      {config.label}
    </Badge>
  );
}
